/**
 * Morphology-agnostic clause/section label detection for table rows.
 * Does not require Hebrew "סעיף" or any fixed prefix word.
 */

const SECTION_NUMBER_RE = /\d+(?:\.\d+)+/g;
const SINGLE_SECTION_NUMBER_RE = /\d+(?:\.\d+)+/;

function normalizeLabelText(text: string): string {
  return text.replace(/[\u200f\u200e\u061c]/g, '').trim();
}

export function extractPrimarySectionNumber(text: string | undefined | null): string {
  const normalized = normalizeLabelText(text || '');
  const match = normalized.match(SINGLE_SECTION_NUMBER_RE);
  return match?.[0] ?? '';
}

function sectionNumberCoverage(text: string): number {
  const normalized = normalizeLabelText(text);
  if (!normalized) return 0;
  const tokens = normalized.match(SECTION_NUMBER_RE) ?? [];
  if (tokens.length === 0) return 0;
  const covered = tokens.reduce((sum, token) => sum + token.length, 0);
  const nonWs = normalized.replace(/\s+/g, '').length;
  return nonWs > 0 ? covered / nonWs : 0;
}

function residualNonLabelText(text: string): string {
  const normalized = normalizeLabelText(text);
  return normalized
    .replace(SECTION_NUMBER_RE, '')
    .replace(/[\s.,\-–—()]+/g, '');
}

/** True when text looks like a short section label (e.g. "14.3", "Section 15.3", "3.24 –"). */
export function isLikelyClauseReferenceText(text: string | undefined | null): boolean {
  const t = (text || '').trim();
  if (!t || t.length > 120) return false;

  const normalized = normalizeLabelText(t);
  const sectionNum = extractPrimarySectionNumber(normalized);
  if (!sectionNum) return false;

  if (t.length <= 30 && /^[\d\s.,\-–—]+$/.test(normalized)) return true;

  if (t.length <= 80 && sectionNumberCoverage(normalized) >= 0.2) {
    if (residualNonLabelText(normalized).length <= 24) return true;
  }

  return false;
}

/** Extract first section number from short label-like context lines. */
export function extractClauseNumberFromLabelContext(rowContextText: string | undefined | null): string {
  const raw = (rowContextText || '').trim();
  if (!raw) return '';

  const candidates: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const t = line.trim();
    if (!t) continue;
    const sectionNum = extractPrimarySectionNumber(t);
    if (!sectionNum) continue;
    if (isLikelyClauseReferenceText(t) || t.length <= 40) {
      candidates.push(sectionNum);
    }
  }

  return candidates[0] ?? '';
}
